import React from 'react';
import type { ClassNameProp, VoidCallback } from '../common-types';
import { StudyCodeList } from './StudyCodeList';
import type { icModel } from 'generated-ts-common';
import { StudyTimeRangeCombo } from './StudyTimeRangeCombo';
import { PhysicianAvatarWithInfo } from '../appointments';
import { AccessibleClickableContainer } from '../accessibility';
import { Button, Icon } from '../basic';
import { intl, intlFmt } from '../../lib';

type GenCode = icModel.saf.infrastructure.model.svs.GenCode;

export type DataDonationProviderProp = {
  firstName: string;
  lastName: string;
  specialization?: string;
  address?: string;
  image?: string;
  onClick(): void;
};

export type DataDonatePolicyMultiSelectProp<T> = {
  available: T;
  selected: T;
  onChange(values: T): void;
};

export type DataDonatePolicyDateRangeProp = {
  available: number[];
  selected: Date | undefined;
  onChange(values: Date | undefined): void;
};

export type DataDonatePolicyProp = ClassNameProp & {
  provider?: DataDonationProviderProp;
  documentTypeCodes: DataDonatePolicyMultiSelectProp<GenCode[]>;
  regionCodes: DataDonatePolicyMultiSelectProp<GenCode[]>;
  purposeCodes: DataDonatePolicyMultiSelectProp<GenCode[]>;
  timeRangePast: DataDonatePolicyDateRangeProp;
  timeRangeFuture: DataDonatePolicyDateRangeProp;
  afterDeath: DataDonatePolicyMultiSelectProp<boolean>;
  readOnly?: boolean;
  allowRevocation?: boolean;
  onRevoke?: VoidCallback;
};

export function DataDonatePolicy({
  className,
  provider,
  documentTypeCodes,
  regionCodes,
  purposeCodes,
  timeRangePast,
  timeRangeFuture,
  afterDeath,
  readOnly,
  allowRevocation,
  onRevoke,
  ...props
}: Readonly<DataDonatePolicyProp>): React.JSX.Element {
  const afterDeathCodes = [
    {
      displayName: intl('datadonation.afterdeath.itemlabel'),
      value: 'after-death',
      codingScheme: 'internal',
      valueMappedAsHL7: ''
    }
  ];
  const afterDeathCodesChanged = (codes: GenCode[]) => {
    const afterDeathSel = codes.length > 0;
    afterDeath.onChange(afterDeathSel);
  };

  return (
    <div className={`ehs-data-donate ${className || ''}`} {...props}>
      {provider && (
        <div className="ehs-data-donate-provider">
          <AccessibleClickableContainer
            className="ehs-data-donate-provider-button"
            onClick={() => provider?.onClick()}
          >
            <PhysicianAvatarWithInfo
              firstName={provider.firstName}
              lastName={provider.lastName}
              specialization={provider.specialization}
              address={provider.address}
              image={provider.image}
              className="s-font font-14"
            />
            <Icon
              iconName="materialicon-chevron_right"
              className="show-details-icon"
              aria-label=""
            />
          </AccessibleClickableContainer>
        </div>
      )}
      <p className="font-16">
        {intl('datadonation.documenttypecodes.description')}
      </p>
      <div className={'ehs-data-donate-policy'}>
        <StudyCodeList
          className="document-types"
          readOnly={readOnly}
          availableCodes={documentTypeCodes.available}
          selectedCodes={documentTypeCodes.selected}
          onChange={documentTypeCodes.onChange}
          data-testid="document-type-code-list"
        />
        <div className="policy-section">
          <StudyTimeRangeCombo
            readOnly={readOnly}
            isTimeRangePast={true}
            className="time-range-past"
            availableRange={timeRangePast.available ?? []}
            selectedRange={timeRangePast.selected}
            onChange={timeRangePast.onChange}
            labelTitle={intl('datadonation.timerange.past.title')}
            labelDescription={intl('datadonation.timerange.past.description')}
            labelCombo={intl('datadonation.timerange.past.combo')}
            labelNoRange={intl('datadonation.timerange.past.norange')}
            labelFctRange={(rangeNr: number) =>
              intlFmt('datadonation.timerange.past.range', rangeNr)
            }
            data-testid="time-range-past-selection"
          />
          <StudyTimeRangeCombo
            readOnly={readOnly}
            isTimeRangePast={false}
            className="time-range-future"
            availableRange={timeRangeFuture.available}
            selectedRange={timeRangeFuture.selected}
            onChange={timeRangeFuture.onChange}
            labelTitle={intl('datadonation.timerange.future.title')}
            labelDescription={intl('datadonation.timerange.future.description')}
            labelCombo={intl('datadonation.timerange.future.combo')}
            labelNoRange={intl('datadonation.timerange.future.norange')}
            labelFctRange={(rangeNr: number) =>
              intlFmt('datadonation.timerange.future.range', rangeNr)
            }
            data-testid="time-range-future-selection"
          />
        </div>
        <StudyCodeList
          readOnly={readOnly}
          availableCodes={afterDeathCodes}
          selectedCodes={afterDeath.selected ? afterDeathCodes : []}
          onChange={afterDeathCodesChanged}
          labelTitle={intl('datadonation.afterdeath.title')}
          labelDescription={intl('datadonation.afterdeath.description')}
          data-testid="after-death-code-list"
        />
        <StudyCodeList
          readOnly={readOnly}
          availableCodes={regionCodes.available}
          selectedCodes={regionCodes.selected}
          onChange={regionCodes.onChange}
          labelTitle={intl('datadonation.region.title')}
          labelDescription={intl('datadonation.region.description')}
          data-testid="region-code-list"
        />
        <StudyCodeList
          readOnly={readOnly}
          availableCodes={purposeCodes.available}
          selectedCodes={purposeCodes.selected}
          onChange={purposeCodes.onChange}
          labelTitle={intl('datadonation.purpose.title')}
          labelDescription={intl('datadonation.purpose.description')}
          data-testid="purpose-code-list"
        />
        <div className="ehs-data-donate-policy-revoke-info font-16">
          <p>{intl('datadonation.revoke.anytime')}</p>
          {allowRevocation && (
            <div>
              <p>{intl('datadonation.revoke.description')}</p>
              <Button
                variant={'tertiary'}
                text={intl('datadonation.revoke.button')}
                scale={'medium'}
                className="ehs-data-donate-policy-button-revoke"
                onClick={onRevoke}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
